// src/pages/Auth.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const AUTH_ERRORS = {
  "auth/user-not-found":      "No account found with this email.",
  "auth/wrong-password":      "Incorrect password.",
  "auth/email-already-in-use":"This email is already registered.",
  "auth/weak-password":       "Password must be at least 6 characters.",
  "auth/invalid-email":       "Please enter a valid email address.",
  "auth/invalid-credential":  "Incorrect email or password.",
  "auth/too-many-requests":   "Too many attempts. Please try again later.",
  "auth/popup-closed-by-user":"Sign-in popup was closed. Please try again.",
};

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const WarnIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const Auth = () => {
  const { user, loginWithGoogle, loginWithEmail, registerWithEmail } = useAuth();
  const navigate = useNavigate();
  const [mode,    setMode]    = useState("login");
  const [form,    setForm]    = useState({ name: "", email: "", password: "" });
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  // Redirect if already logged in
  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);

  const change = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !form.name.trim()) {
      return setError("Please enter your full name.");
    }
    setLoading(true);
    setError("");
    try {
      if (mode === "login") {
        await loginWithEmail(form.email, form.password);
      } else {
        await registerWithEmail(form.email, form.password, form.name.trim());
      }
      navigate("/");
    } catch (err) {
      setError(AUTH_ERRORS[err.code] || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    setError("");
    try {
      await loginWithGoogle();
      navigate("/");
    } catch (err) {
      setError(AUTH_ERRORS[err.code] || "Google sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(m => m === "login" ? "register" : "login");
    setError("");
    setForm({ name: "", email: "", password: "" });
  };

  return (
    <div className="auth-page">
      {/* Left art panel */}
      <div className="auth-art" aria-hidden="true">
        <div className="auth-art__grid">
          {Array.from({ length: 24 }).map((_, i) => (
            <div key={i} className="auth-art__cell" style={{ animationDelay: `${(i * 0.18) % 4}s` }} />
          ))}
        </div>
        <div className="auth-art__overlay">
          <blockquote className="auth-art__quote">
            "Photography is the art of frozen time — the ability to store emotion and feelings within a frame."
          </blockquote>
          <cite className="auth-art__cite">— Meshack Otieno</cite>
        </div>
      </div>

      {/* Right form panel */}
      <div className="auth-panel">
        <div className="auth-panel__inner">
          <a href="/" className="auth-logo">
            <span className="logo-mark">HI</span>
            <span className="logo-name">Halro Images</span>
          </a>

          <h1 className="auth-panel__title">
            {mode === "login" ? "Welcome back" : "Create account"}
          </h1>
          <p className="auth-panel__sub">
            {mode === "login"
              ? "Sign in to access your download history."
              : "Join Halro Images and start downloading stunning photos."}
          </p>

          {/* Google */}
          <button className="btn-google" onClick={handleGoogle} disabled={loading}>
            <GoogleIcon />
            Continue with Google
          </button>

          <div className="auth-divider"><span>or continue with email</span></div>

          {/* Error */}
          {error && (
            <div className="auth-error" role="alert">
              <WarnIcon />
              {error}
            </div>
          )}

          {/* Form */}
          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            {mode === "register" && (
              <div className="field">
                <label className="field__label" htmlFor="name">Full Name</label>
                <input
                  id="name" name="name" type="text"
                  className="field__input"
                  placeholder="Jane Smith"
                  value={form.name}
                  onChange={change}
                  autoComplete="name"
                  required
                  disabled={loading}
                />
              </div>
            )}

            <div className="field">
              <label className="field__label" htmlFor="email">Email</label>
              <input
                id="email" name="email" type="email"
                className="field__input"
                placeholder="jane@example.com"
                value={form.email}
                onChange={change}
                autoComplete="email"
                required
                disabled={loading}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="password">Password</label>
              <div className="field__input-wrap">
                <input
                  id="password" name="password"
                  type={showPwd ? "text" : "password"}
                  className="field__input field__input--password"
                  placeholder={mode === "register" ? "At least 6 characters" : "Your password"}
                  value={form.password}
                  onChange={change}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="field__toggle-pwd"
                  onClick={() => setShowPwd(v => !v)}
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary btn-full"
              disabled={loading}
              style={{ marginTop: 6 }}
            >
              {loading
                ? <span className="spinner" />
                : mode === "login" ? "Sign In" : "Create Account"
              }
            </button>
          </form>

          <p className="auth-switch">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button className="auth-switch__btn" onClick={switchMode} type="button">
              {mode === "login" ? "Sign up free" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
